<script setup>
import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import { onClickOutside } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

const dropdownRef = ref(null)
const isOpen = ref(false)

const { t } = useI18n()

onClickOutside(dropdownRef, () => {
  isOpen.value = false
})
</script>

<template>
  <div class="hidden lg:flex items-center relative" ref="dropdownRef">
    <button
      @click="isOpen = !isOpen"
      class="flex items-center space-x-1.5 px-3 py-2 rounded-lg text-brand-muted hover:text-brand-accent hover:bg-brand-muted/10 transition-all duration-200 group font-semibold"
    >
      <span>{{ t('footer.explore') }}</span>
      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 transition-transform duration-200" :class="{ 'rotate-180': isOpen }" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <div
        v-show="isOpen"
        class="absolute top-full left-0 mt-2 w-56 bg-m3-surface rounded-3xl shadow-lg shadow-black/15 border border-m3-outline-variant overflow-hidden z-50"
        role="menu"
      >
        <div class="py-2">
          <!-- Albums -->
          <RouterLink
            to="/albums"
            @click="isOpen = false"
            class="flex items-center gap-3 px-4 py-3 text-sm text-brand-text hover:bg-brand-muted/10 hover:text-brand-accent transition-colors min-h-[48px]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
            {{ t('nav.albums') }}
          </RouterLink>

          <!-- Releases -->
          <RouterLink
            to="/releases"
            @click="isOpen = false"
            class="flex items-center gap-3 px-4 py-3 text-sm text-brand-text hover:bg-brand-muted/10 hover:text-brand-accent transition-colors min-h-[48px]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {{ t('nav.releases') }}
          </RouterLink>

          <!-- Charts -->
          <RouterLink
            to="/charts"
            @click="isOpen = false"
            class="flex items-center gap-3 px-4 py-3 text-sm text-brand-text hover:bg-brand-muted/10 hover:text-brand-accent transition-colors min-h-[48px]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z"/>
              <path d="M19 19H5v-3h14v3z" opacity="0.5"/>
            </svg>
            {{ t('nav.charts') }}
          </RouterLink>

          <!-- Concerts (coming soon) -->
          <div
            class="flex items-center gap-3 px-4 py-3 text-sm text-brand-muted/50 cursor-not-allowed min-h-[48px]"
            :title="t('nav.coming_soon')"
            aria-disabled="true"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span>{{ t('nav.concerts') }}</span>
            <span class="ml-auto text-[10px] font-bold uppercase tracking-widest text-brand-muted/40">{{ t('nav.soon') }}</span>
          </div>

          <!-- Festivals -->
          <RouterLink
            to="/festivals"
            @click="isOpen = false"
            class="flex items-center gap-3 px-4 py-3 text-sm text-brand-text hover:bg-brand-muted/10 hover:text-brand-accent transition-colors min-h-[48px]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            {{ t('nav.festivals') }}
          </RouterLink>
        </div>
      </div>
    </Transition>
  </div>
</template>
